import { isIPAddress } from "ip-address-validator";
import { apiPost, getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { pushErrorToMessageRegistery } from "userful-chronos-app-common-js/dist/message/messageRegistery";
import { ServiceReply, UIReply } from "userful-chronos-app-common-js/dist/models/common";
import { HttpsSettings } from "userful-chronos-app-common-js/dist/models/config/httpsSettings";
import { initSSHUserCreate } from "userful-chronos-app-common-js/dist/models/config/sshUsers";
import { AppStatus } from "userful-chronos-app-common-js/dist/models/postLoginData";
import { getRootPath } from "userful-chronos-app-common-js/dist/routing";

const getDefaultHTTPSURL = () => {
    const currentHost = getGlobalStates().hostWidthoutProtocol;
    if (!currentHost) {
        return "https://";
    }
    if (isIPAddress(currentHost) || currentHost.toLowerCase().includes("localhost")) {
        return "https://";
    }
    return `https://${currentHost}${getRootPath()}`;
};

export const initHttpsSettings = (appStatus?: AppStatus): HttpsSettings => {
    const frontEndUrl = getDefaultHTTPSURL();
    const enableSSL = true;
    const selfSigned = appStatus ? appStatus.appCertStatus.certStatus.selfSigned : false;
    const defaultSSHUserEnable = !!appStatus?.defaultSSHUserEnable;
    const defaultSshUser = initSSHUserCreate();
    const sshUser = defaultSSHUserEnable
        ? {
              ...defaultSshUser,
              id: appStatus?.systemID || defaultSshUser.id,
              username: "userful",
              userfulKeys: true,
          }
        : defaultSshUser;
    return {
        key: appStatus?.appCertStatus.certStatus.key || "",
        cert: appStatus?.appCertStatus.certStatus.cert || "",
        enableSSL,
        frontEndUrl,
        selfSigned,
        organization: appStatus?.keycloakStatus.organization || "",
        serverDeploymentType: appStatus?.keycloakStatus.serverDeploymentType || "ONPREM",
        sshUser,
    };
};

const UPDATE_SETTINGS_URI = "/init/setup/auth/update";
const INITIAL_SETUP_URI = "/init/setup/auth/setup";

export interface HttpsSettingsError {
    enableSSL?: string;
    frontEndUrl?: string;
    cert?: string;
    key?: string;
}

export const sanitizeHttpsSettings = (newSettings: HttpsSettings): HttpsSettings => {
    const url = newSettings.frontEndUrl.replace("http://", "https://");
    return {
        ...newSettings,
        frontEndUrl: url.startsWith("https://") ? url : `https://${url}`,
    };
};
const IP_TEST =
    /^((?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])[.]){3}(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])$/;

export const getHttpsErrors = (newSettings: HttpsSettings): HttpsSettingsError => {
    let urlError = "";
    try {
        const url = new URL(newSettings.frontEndUrl);
        if (isIPAddress(url.hostname) || url.hostname.toLowerCase() === "localhost") {
            urlError = "CommonUI.Config.Https.urlError";
        }
    } catch (err) {
        urlError = "CommonUI.Config.Https.urlMissing";
    }
    if (newSettings.frontEndUrl && !newSettings.frontEndUrl.toLowerCase().startsWith("https://")) {
        urlError = "CommonUI.Config.Https.urlHttps";
    }
    return {
        frontEndUrl: urlError,
        cert: !newSettings.selfSigned && !newSettings.cert ? "CommonUI.Config.Https.certMissing" : "",
        key: !newSettings.selfSigned && !newSettings.key ? "CommonUI.Config.Https.keyMissing" : "",
    };
};

// connection is immediately cut off after sending the https settings, so this evil is necessary ...
const redirectToNewURLAfterTimeout = (url: string) => {
    setTimeout(() => {
        window.location.replace(url);
    }, 20000);
};
const redirectToNewURLAfterTimeoutOnError = (url: string) => {
    setTimeout(() => {
        window.location.replace(url);
    }, 30000);
};

export const applyHttpsSettingsToServer = (settings: HttpsSettings, initialSetupToken: string) => {
    let error;

    if (initialSetupToken) {
        apiPost(getGlobalStates().host + INITIAL_SETUP_URI, settings, {
            headers: {
                Authorization: `Bearer ${initialSetupToken}`,
            },
        })
            .then((data) => {
                const result = data.data as ServiceReply<any>;

                if (result.severity !== "SUCCESS") {
                    console.debug("FAIL!", result);

                    pushErrorToMessageRegistery({
                        ...result,
                        msg: result.unlocalizedMsg,
                        success: false,
                    } as unknown as UIReply<any>);
                } else {
                    redirectToNewURLAfterTimeout(settings.frontEndUrl);
                }
            })
            .catch((e) => {
                redirectToNewURLAfterTimeoutOnError(settings.frontEndUrl);

                return { unlocalizedMsg: "Error applying https settings: " };
            });

        return error;
    }

    console.warn("Applying https settings ...");
    apiPost(
        getGlobalStates().host + UPDATE_SETTINGS_URI,
        {
            ...settings,
            cert: settings.cert,
            key: settings.key,
        },
        {
            headers: {
                Authorization: `Bearer ${getGlobalStates().keycloak.token}`,
            },
        },
    )
        .then(() => {
            // console.log("Redirecting to new url ...");
            redirectToNewURLAfterTimeout(settings.frontEndUrl);
        })
        .catch(() => {
            // console.log("Redirecting to new url ...");
            redirectToNewURLAfterTimeoutOnError(settings.frontEndUrl);
        });
};
